'use client'

import { useEffect, useRef, useCallback } from 'react'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { useTranslations } from 'next-intl'
import { useActivityVisibility } from '@/hooks/useActivityVisibility'
import NextLink from 'next/link'
import { Link as I18nLink } from '@/i18n/navigation'
import { useIsI18nContext } from '@/hooks/useIsI18nContext'
import { lodges } from '@/lib/data/lodges'
import './LodgesSlideshow.css'

// Cache bust — prevents browsers from serving stale cached 404s
// Bump this value on each deploy when image paths change
const IMG_VERSION = '20260409'
const bustSrc = (url: string) =>
  url.startsWith('http') ? url : `${url}?v=${IMG_VERSION}`

// Badge config per lodge — labelKey maps to homepage.destinationsSection.badges.*
const badgeConfig: Record<string, { labelKey: string; comingSoon?: boolean }> = {
  'apex-mountain-penthouse': { labelKey: 'badges.skiInSkiOut' },
  'apex-yelcho-patagonia': { labelKey: 'badges.wildernessLodge' },
  'apex-patagonia-lodge': { labelKey: 'badges.flyFishing' },
  'apex-lakefront-serenity': { labelKey: 'badges.comingSoon', comingSoon: true },
  'apex-parva-cave': { labelKey: 'badges.mountainRetreat' },
  'apex-premium-lodge': { labelKey: 'badges.snowSeason2027', comingSoon: true },
}

// Featured lodges get a subtle glow border
const featuredIds = new Set(['apex-mountain-penthouse', 'apex-premium-lodge'])

const LocationPinIcon = () => (
  <svg className="location-pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
)

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
)

export default function LodgesSlideshow() {
  const sectionRef = useScrollAnimation<HTMLElement>({
    threshold: 0.1,
    rootMargin: '0px 0px 150px 0px'
  })

  const isI18n = useIsI18nContext()
  const Link = isI18n ? I18nLink : NextLink
  const tSection = useTranslations('homepage.destinationsSection')
  const { isVisible } = useActivityVisibility()

  const desktopCardsRef = useRef<(HTMLAnchorElement | null)[]>([])

  // Filter lodges by admin visibility (Firestore itemVisibility)
  // NOTE: Do NOT pass comingSoon as staticHidden — coming soon lodges must appear.
  // Only lodges explicitly set to false in Firestore itemVisibility are hidden.
  const visibleLodges = lodges.filter(lodge => isVisible(lodge.id))
  const lodgeCount = visibleLodges.length

  // Staggered card reveal on scroll (desktop grid)
  useEffect(() => {
    // Reset stale refs when lodge count changes
    desktopCardsRef.current = desktopCardsRef.current.slice(0, lodgeCount)

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const card = entry.target as HTMLElement
            const idx = parseInt(card.dataset.cardIndex || '0', 10)
            card.style.transitionDelay = `${idx * 0.12}s`
            card.classList.add('card-visible')
            observer.unobserve(card)
          }
        })
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    )

    desktopCardsRef.current.forEach((card) => {
      if (card) observer.observe(card)
    })

    return () => observer.disconnect()
  }, [lodgeCount])

  // Retry failed images with cache-busting
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (!img.dataset.retried) {
      img.dataset.retried = '1'
      const base = img.dataset.src || img.src
      const sep = base.includes('?') ? '&' : '?'
      img.src = `${base}${sep}_cb=${Date.now()}`
    }
  }, [])

  // Parallax hover (desktop only)
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const card = e.currentTarget
    const img = card.querySelector('.card-image') as HTMLImageElement | null
    if (!img) return
    const rect = card.getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    img.style.transform = `scale(1.08) translate(${(x - 0.5) * -12}px, ${(y - 0.5) * -12}px)`
  }, [])

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    const img = e.currentTarget.querySelector('.card-image') as HTMLImageElement | null
    if (img) img.style.transform = 'scale(1) translate(0, 0)'
  }, [])

  if (lodgeCount === 0) return null

  const lastIndex = lodgeCount - 1

  return (
    <section className="lodge-gallery" data-animate-on-scroll ref={sectionRef}>
      <div className="container">

        {/* Section Header */}
        <header className="gallery-header" data-scroll-item>
          <p className="gallery-eyebrow">{tSection('title')}</p>
          <h2 className="gallery-title">{tSection('galleryTitle')}</h2>
          <p className="gallery-description">{tSection('description')}</p>
        </header>

        {/* ═══════ DESKTOP BENTO GRID ═══════
            Visible on tablet+ (≥769px). Pure CSS handles layout
            via data-lodge-count attribute — no JS positioning needed.
            ═══════════════════════════════════ */}
        <div
          className="desktop-grid"
          data-lodge-count={lodgeCount}
          data-scroll-item
          role="list"
          aria-label={tSection('lodgeDestinations')}
        >
          {visibleLodges.map((lodge, index) => {
            const badge = badgeConfig[lodge.id]
            const isFeatured = featuredIds.has(lodge.id)

            return (
              <Link
                key={lodge.id}
                href={`/lodges/${lodge.slug}`}
                className={`lodge-card${isFeatured ? ' featured' : ''}`}
                role="listitem"
                data-card-index={index}
                ref={(el) => { desktopCardsRef.current[index] = el as HTMLAnchorElement | null }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                prefetch={false}
              >
                <div className="card-image-wrapper">
                  <img
                    className="card-image"
                    src={bustSrc(lodge.heroImage)}
                    data-src={lodge.heroImage}
                    alt={`${lodge.name} - ${lodge.subtitle}`}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    onError={handleImageError}
                  />
                </div>
                <div className="card-overlay" />
                <div className="card-content">
                  <div className="card-top">
                    <span className="card-location">
                      <LocationPinIcon />
                      {lodge.location}
                    </span>
                    {badge?.labelKey && (
                      <span className={`card-badge${badge.comingSoon ? ' coming-soon' : ''}`}>
                        {tSection(badge.labelKey)}
                      </span>
                    )}
                  </div>
                  <div className="card-bottom">
                    <h3 className="lodge-name">{lodge.name}</h3>
                    <p className="lodge-subtitle">{lodge.subtitle}</p>
                  </div>
                </div>
                <div className="card-explore">
                  <span>{tSection('explore')}</span>
                  <span className="explore-arrow"><ArrowIcon /></span>
                </div>
              </Link>
            )
          })}
        </div>

        {/* ═══════ MOBILE LAYOUT (v2 — Magazine 2-Column Grid) ═══════
            Visible on mobile only (≤768px).
            First card = full-width hero. Rest = 2-column image grid.
            Last card spans full width if alone on its row.
            ═══════════════════════════════════════════════════════════════ */}
        <div className="mobile-layout" data-scroll-item role="list" aria-label={tSection('lodgeDestinations')}>
          {visibleLodges.map((lodge, i) => {
            const badge = badgeConfig[lodge.id]
            const isHero = i === 0
            const isFooter = i === lastIndex && i % 2 === 0

            return (
              <Link
                key={lodge.id}
                href={`/lodges/${lodge.slug}`}
                className={`mobile-card${isHero ? ' mobile-card--hero' : ''}${isFooter ? ' mobile-card--footer' : ''}`}
                role="listitem"
                prefetch={false}
              >
                <img
                  className="mobile-card-img"
                  src={bustSrc(lodge.heroImage)}
                  data-src={lodge.heroImage}
                  alt={`${lodge.name} - ${lodge.subtitle}`}
                  loading="eager"
                  decoding="async"
                  onError={handleImageError}
                />
                <div className="mobile-card-overlay" />
                <div className="mobile-card-inner">
                  <div className="mobile-card-top">
                    {badge?.labelKey && (
                      <span className={`mobile-card-badge${badge.comingSoon ? ' coming-soon' : ''}`}>
                        {tSection(badge.labelKey)}
                      </span>
                    )}
                  </div>
                  <div className="mobile-card-bottom">
                    <p className="mobile-card-location">{lodge.location}</p>
                    <h3 className="mobile-card-name">{lodge.name}</h3>
                    {isHero && <p className="mobile-card-sub">{lodge.subtitle}</p>}
                  </div>
                </div>
              </Link>
            )
          })}
        </div>

        {/* CTA */}
        <div className="gallery-cta" data-scroll-item>
          <Link href="/lodges" className="btn-explore" prefetch={false}>
            <span>{tSection('cta')}</span>
            <svg className="btn-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

      </div>
    </section>
  )
}
